import Auth from "../../auth/Auth";

const Http = () => {
  /**
   * Generate headers for HTTP requests.
   * @param {Object} options - Options for generating headers.
   * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE, etc.).
   * @param {string} [options.contentType="application/json"] - Content type of the request (default: JSON).
   * @param {Object} [options.body] - Request body.
   * @param {boolean} [options.isSecure] - Boolean indicating if the request should be secure (authenticated).
   * @returns {Object} Headers object for the HTTP request.
   */
  const getHeaders = ({
    method,
    contentType = "application/json",
    body,
    isSecure,
  }) => ({
    method: method,
    headers: {
      Accept: "application/json",
      ...(contentType === "application/json" && {
        "Content-Type": contentType,
      }),
      ...(Auth.token() && { Authorization: "Bearer " + Auth.token() }),
    },
    ...(body && {
      body: contentType === "application/json" ? JSON.stringify(body) : body,
    }),
  });

  /**
   * Handle HTTP response.
   * @param {Response} response - The HTTP response.
   * @param {Function} navigate - Function to navigate within the application.
   * @param {Object} body - The request body.
   * @param {string} [responseType] - Expected response type (e.g., 'blob').
   * @returns {Promise<Object|Blob>} Parsed response data or error.
   */
  const handleResponse = async (response, navigate, body, responseType) => {
    /**
     * Build an Error that also carries the HTTP status, so callers can react to
     * specific codes (402/410/422) without parsing the message text.
     */
    const httpError = (message, status, options) => {
      const error = new Error(message, options);
      error.status = status;
      return error;
    };

    if (response.status === 201 || response.status === 204) {
      return;
    }

    if (responseType === "blob") {
      if (response.status === 200) {
        return await response.blob();
      } else {
        throw new Error("Error: Response status not 200");
      }
    }

    const res = await response.json();
    if (response.status >= 200 && response.status <= 299) {
      return res;
    } else if (response.status === 401) {
      Auth.logout();
      navigate("/");
      throw httpError(res.message || "Unauthorized. Logging out...", 401, {
        cause: "UNAUTHENTICATED",
      });
    } else if (response.status === 400) {
      if (res?.code === "EMAIL_NOT_VERIFIED") {
        navigate("/verification", {
          state: { email: body?.email, type: "signup" },
        });
      } else if (res?.code === "FORGET_PASSWORD_OTP_EXPIRED") {
        navigate("/verification", {
          state: { email: body?.email, type: "forgot" },
        });
      }
      throw httpError(res.message || "An error occurred.", response.status);
    } else {
      throw httpError(res.message || "An error occurred.", response.status);
    }
  };

  /**
   * Make an HTTP request.
   * @param {Object} options - Options for the HTTP request.
   * @param {string} options.method - HTTP method (GET, POST, PUT, DELETE, etc.).
   * @param {string} options.url - The URL to send the request to.
   * @param {Object} [options.body] - The request body.
   * @param {boolean} [options.isSecure] - Boolean indicating if the request should be secure (authenticated).
   * @param {string} [options.contentType="application/json"] - Content type of the request (default: JSON).
   * @param {string} [options.responseType] - Expected response type (e.g., 'blob').
   * @param {Function} navigate - Function to navigate within the application.
   * @returns {Promise<Object|Blob>} Parsed response data or blob.
   */
  const httpRequest = async (
    { method, url, body, isSecure, contentType, responseType },
    navigate = () => {}
  ) => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}${url}`,
      getHeaders({ method, contentType, body, isSecure })
    );
    return await handleResponse(response, navigate, body, responseType);
  };

  return {
    httpRequest,
  };
};

export default Http;